import React from 'react';
import { CONTROL_BASE, controlState } from './fieldStyles';

/**
 * Largura do campo pela natureza do dado, não pela conveniência do layout.
 * Um CEP não precisa (nem deve) ocupar a linha inteira.
 *   xs  UF, número, CVV      sm  data, CEP, hora
 *   md  CPF, telefone, valor lg  CNPJ, e-mail curto      full  texto livre (padrão)
 *   auto  sem largura própria — o pai decide (filtros em linha flex)
 * Todas encolhem em tela estreita (`w-full max-w-*`).
 */
export type FieldWidth = 'xs' | 'sm' | 'md' | 'lg' | 'full' | 'auto';

const WIDTH: Record<FieldWidth, string> = {
  xs: 'w-full max-w-[6rem]',
  sm: 'w-full max-w-[10rem]',
  md: 'w-full max-w-[14rem]',
  lg: 'w-full max-w-[20rem]',
  full: 'w-full',
  /** Sem largura própria: quem manda é o pai (linhas flex de filtros, `flex-1 min-w-*`). */
  auto: '',
};

interface CommonProps {
  fieldWidth?: FieldWidth;
  invalid?: boolean;
}

// ── Input ────────────────────────────────────────────────────────────────────

// ComponentProps (não ...WithoutRef): no React 19 `ref` é prop comum e chega ao <input> via `...rest`.
interface InputProps extends Omit<React.ComponentProps<'input'>, 'size'>, CommonProps {
  /** Ícone à esquerda (decorativo). */
  startIcon?: React.ReactNode;
  /** Elemento à direita, ex.: botão de mostrar senha. Deve ser clicável por si só. */
  endAdornment?: React.ReactNode;
}

export const Input: React.FC<InputProps> = ({
  fieldWidth = 'full',
  invalid,
  startIcon,
  endAdornment,
  className = '',
  ...rest
}) => (
  <div className={`relative ${WIDTH[fieldWidth]}`}>
    {startIcon && (
      <span aria-hidden="true" className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-fg-subtle">
        {startIcon}
      </span>
    )}
    <input
      aria-invalid={invalid || undefined}
      className={`${CONTROL_BASE} ${controlState(invalid)} h-control w-full ${startIcon ? 'pl-10' : 'pl-3'} ${endAdornment ? 'pr-11' : 'pr-3'} ${className}`}
      {...rest}
    />
    {endAdornment && (
      <span className="absolute inset-y-0 right-1 flex items-center">{endAdornment}</span>
    )}
  </div>
);

// ── Select ───────────────────────────────────────────────────────────────────

interface SelectProps extends Omit<React.ComponentPropsWithoutRef<'select'>, 'size'>, CommonProps {}

export const Select: React.FC<SelectProps> = ({
  fieldWidth = 'full',
  invalid,
  className = '',
  children,
  ...rest
}) => (
  <select
    aria-invalid={invalid || undefined}
    className={`${CONTROL_BASE} ${controlState(invalid)} h-control px-3 ${WIDTH[fieldWidth]} ${className}`}
    {...rest}
  >
    {children}
  </select>
);

// ── Textarea ─────────────────────────────────────────────────────────────────

interface TextareaProps extends React.ComponentPropsWithoutRef<'textarea'>, CommonProps {}

export const Textarea: React.FC<TextareaProps> = ({
  fieldWidth = 'full',
  invalid,
  className = '',
  ...rest
}) => (
  <textarea
    aria-invalid={invalid || undefined}
    className={`${CONTROL_BASE} ${controlState(invalid)} min-h-[5rem] px-3 py-2 ${WIDTH[fieldWidth]} ${className}`}
    {...rest}
  />
);

// ── FormField: rótulo + controle + dica/erro ─────────────────────────────────

interface FormFieldProps {
  /** Deve ser o `id` do controle filho — é o que liga rótulo e campo. */
  htmlFor: string;
  label: React.ReactNode;
  hint?: React.ReactNode;
  error?: React.ReactNode;
  required?: boolean;
  /** Conteúdo à direita do rótulo, ex.: link "Esqueci minha senha". */
  labelAside?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}

export const FormField: React.FC<FormFieldProps> = ({
  htmlFor, label, hint, error, required, labelAside, children, className = '',
}) => (
  <div className={className}>
    <div className="mb-1.5 flex items-baseline justify-between gap-3">
      <label htmlFor={htmlFor} className="block text-sm font-medium text-fg">
        {label}
        {required && <span aria-hidden="true" className="ml-0.5 text-danger">*</span>}
      </label>
      {labelAside}
    </div>
    {children}
    {error ? (
      <p role="alert" className="mt-1.5 text-xs text-danger-text">{error}</p>
    ) : hint ? (
      <p className="mt-1.5 text-xs text-fg-muted">{hint}</p>
    ) : null}
  </div>
);
